import React, { useState, useRef, useEffect } from 'react';
import {
  Sparkles,
  MessageSquare,
  Send,
  X,
  ChevronDown,
  FileText,
  Activity,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  ExternalLink,
  Bot,
  User,
  Stethoscope
} from 'lucide-react';
import { Patient, ClinicalDocument } from '../types';
import { GoogleGenAI } from '@google/genai';

interface Message {
  id: string;
  sender: 'user' | 'assistant';
  timestamp: string;
  text: string;
  patientContext?: {
    name: string;
    mrn: string;
    status: string;
    department: string;
  };
}

interface AiClinicalAssistantProps {
  patients: Patient[];
  onSelectPatient?: (patient: Patient) => void;
  onOpenDocs?: () => void;
}

const DEFAULT_SUGGESTIONS = [
  'Find the critical patient note',
  'Find the Sophia Chen note',
  'ICU / CCU Census status',
  'Check Harold Greenfield Sentry alert'
];

export const AiClinicalAssistant: React.FC<AiClinicalAssistantProps> = ({
  patients,
  onSelectPatient,
  onOpenDocs
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome-1',
      sender: 'assistant',
      timestamp: 'Just now',
      text: 'Hello Dr. Sterling. I am **MediCore AI Clinical Assistant**, grounded in your hospital census, real-time inpatient telemetry feeds, and clinical documents repository. How can I assist you with clinical triage or note discovery today?'
    }
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSendQuery = async (queryText: string) => {
    if (!queryText.trim()) return;

    const userMsg: Message = {
      id: `msg-${Date.now()}`,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      text: queryText
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    // Clinical Logic / Grounding Engine
    const lower = queryText.toLowerCase();

    // Small delay to simulate clinical synthesis
    setTimeout(async () => {
      let responseText = '';
      let context: Message['patientContext'] | undefined;

      if (lower.includes('critical') && lower.includes('note')) {
        // Critical patient note inquiry
        responseText = `### Critical Patient Clinical Note (SOAP)
**Patient:** Eleanor Vance (MRN: \`MC-84920-E\`)
**Location:** Intensive Care Unit (Bed ICU-B04) • **Attending:** Dr. Marcus Sterling, MD
**Status:** **CRITICAL** (Triage Priority 1)

* **Subjective:** 64-year-old female admitted via trauma bay with acute substernal crushing chest pain radiating to the left jaw, marked diaphoresis, and shortness of breath (rated 9/10).
* **Objective:** BP 88/54 mmHg, HR 118 bpm (sinus tachycardia), SpO2 92% on 2L NC, Temp 38.2°C, RR 26/min. ECG demonstrates 3mm ST-elevations in leads II, III, aVF. High-sensitivity Troponin T: **1,840 ng/L** (Critical High), Lactate: 3.1 mmol/L.
* **Assessment:**
  1. Acute Inferior STEMI complicated by Killip Class III Cardiogenic shock.
  2. Lactic acidosis secondary to hypoperfusion.
* **Plan:** STAT Interventional Cardiology consult for urgent PCI; continuous IV Norepinephrine titration (target MAP > 65 mmHg); Dual antiplatelet therapy administered (Aspirin 325 mg + Ticagrelor 180 mg). Continuous hemodynamic telemetry.`;

        context = {
          name: 'Eleanor Vance',
          mrn: 'MC-84920-E',
          status: 'Critical',
          department: 'Intensive Care'
        };
      } else if (lower.includes('sophia chen') || lower.includes('sophia')) {
        // Sophia Chen note inquiry
        responseText = `### Clinical Note: Sophia Chen (MRN: \`MC-99412-S\`)
**Age/Gender:** 42 F • **Location:** Step-Down Wing (Bed SD-104)
**Encounter:** Post-Operative Day 3 Follow-Up — Laparoscopic Cholecystectomy
**Attending:** Dr. David Chen, MD • **Status:** Stable

* **Subjective:** Patient reports surgical pain well-managed with oral acetaminophen (rated 2/10). Tolerating regular diet with no nausea or abdominal distension. Ambulating corridors independently.
* **Objective:** Vitals: BP 120/78 mmHg, HR 72 bpm, SpO2 99% on room air, Temp 36.8°C. Abdomen soft, non-tender; four laparoscopic port sites clean, dry, and intact with no erythema or discharge. Active bowel sounds in all 4 quadrants.
* **Assessment:** Uncomplicated recovery following elective laparoscopic cholecystectomy for symptomatic cholelithiasis.
* **Plan:**
  1. Clear for discharge home today pending adult transport.
  2. Discontinue IV access; transition to oral analgesics PRN.
  3. Outpatient surgical follow-up in clinic in 14 days.`;

        context = {
          name: 'Sophia Chen',
          mrn: 'MC-99412-S',
          status: 'Stable / Discharge Ready',
          department: 'Step-Down Surgical'
        };
      } else if (lower.includes('harold greenfield') || lower.includes('sentry') || lower.includes('suite 112')) {
        responseText = `### Early Deterioration Sentry: Harold Greenfield (Suite 112)
**Metric Alert:** Subclinical Physiological & Behavioral Deviation (Risk Score: **84 / 100**)

* **Biomarker Deviation:** Ambient mmWave radar telemetry detected a 15% reduction in average gait velocity (0.92 m/s down to 0.78 m/s).
* **Behavioral Pattern:** Nighttime bathroom visits doubled over the past 48 hours (3 vs baseline 1.2).
* **Clinical Synthesis:** Pattern strongly suggests subclinical urinary tract infection (UTI) with impending acute delirium before overt fever or hypotension manifest.
* **Recommended Standing Orders:**
  1. Urinalysis with micro & reflex culture.
  2. Bedside fall precautions elevated to High.
  3. Nursing cognitive assessment check (CAM scale).`;
      } else if (lower.includes('icu') || lower.includes('census') || lower.includes('capacity')) {
        responseText = `### Executive Census & Department Capacity
* **Intensive Care Unit (ICU):** 16 / 18 Beds Occupied (**88.9%**) — *Near Capacity*. 2 Critical cases requiring 1:1 nursing.
* **Cardiology & Telemetry:** 28 / 32 Beds Occupied (**87.5%**).
* **Step-Down Surgical:** 24 / 28 Beds Occupied (**85.7%**).
* **Emergency Department:** 14 active acute holds in trauma bays.
* **Operational Recommendation:** Expedite discharge orders for 2 qualified step-down patients (including Sophia Chen) to free telemetry monitors for oncoming transfers.`;
      } else {
        // Try Gemini if possible, or provide clinical response
        try {
          const apiKey = (window as any).GEMINI_API_KEY || '';
          if (apiKey) {
            const ai = new GoogleGenAI({ apiKey, vertexai: true });
            const prompt = `You are MediCore AI Clinical Assistant at Metropolitan Memorial Trauma Hub.
The current inpatient census includes:
- Eleanor Vance (MRN: MC-84920-E, 64F, Intensive Care, Critical STEMI + Cardiogenic shock, Dr. Sterling)
- Julian Hayes (MRN: MC-91024-C, 48M, Cardiology, Guarded A-fib with RVR, Dr. Lin)
- Samantha Rivera (MRN: MC-72991-N, 32F, Neurology, Stable Migraine with aura, Dr. Vance)
- Arthur Pendelton (MRN: MC-64819-E, 72M, Emergency, Resolved COPD, Dr. Chen)
- Sophia Chen (MRN: MC-99412-S, 42F, Step-Down, Post-op Day 3 Lap Cholecystectomy, stable)

User query: "${queryText}"
Provide a concise, board-certified clinical response in GitHub markdown format.`;
            const result = await ai.models.generateContent({
              model: 'gemini-2.5-flash',
              contents: prompt
            });
            responseText = result.text || 'Telemetry and records synchronized.';
          } else {
            // Contextual search fallback
            const matchPatient = patients.find(
              (p) =>
                lower.includes(p.fullName.toLowerCase()) ||
                lower.includes(p.mrn.toLowerCase()) ||
                lower.includes(p.department.toLowerCase())
            );

            if (matchPatient) {
              responseText = `### Clinical Profile: ${matchPatient.fullName}
* **MRN:** \`${matchPatient.mrn}\` | **Age/Sex:** ${matchPatient.age} ${matchPatient.gender}
* **Department:** ${matchPatient.department} (${matchPatient.bedNumber})
* **Attending Physician:** ${matchPatient.attendingPhysician}
* **Triage Status:** **${matchPatient.status}** (Priority ${matchPatient.triageScore})
* **Diagnosis:** ${matchPatient.primaryDiagnosis}
* **Telemetry Vitals:**
  * Heart Rate: **${matchPatient.vitals.heartRate} bpm**
  * Blood Pressure: **${matchPatient.vitals.bpSystolic}/${matchPatient.vitals.bpDiastolic} mmHg**
  * SpO2: **${matchPatient.vitals.spO2}%** | Temp: **${matchPatient.vitals.tempC}°C** | Resp Rate: **${matchPatient.vitals.respRate}/min**
* **Known Allergies:** ${matchPatient.allergies.join(', ')}`;

              context = {
                name: matchPatient.fullName,
                mrn: matchPatient.mrn,
                status: matchPatient.status,
                department: matchPatient.department
              };
            } else {
              responseText = `Clinical inquiry processed for query: *"${queryText}"*. All inpatient monitors, ambient sensors in Rooms 101–103, and certified EHR documents are currently synchronized with the Executive Intelligence Center. You can select specific patient files or request automated SOAP notes from the navigation menu.`;
            }
          }
        } catch (err) {
          responseText = `Clinical telemetry synchronized. Query received: *"${queryText}"*. Inpatient registry and telemetry feeds are operating nominally under DEFCON-4.`;
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `resp-${Date.now()}`,
          sender: 'assistant',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          text: responseText,
          patientContext: context
        }
      ]);
      setIsTyping(false);
    }, 450);
  };

  return (
    <>
      {/* Floating Trigger Button */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white rounded-full shadow-2xl shadow-teal-900/50 border border-teal-400/30 transition-all transform hover:scale-105 group"
          >
            <div className="relative">
              <Sparkles className="w-5 h-5 text-amber-300 animate-pulse" />
              <span className="absolute -top-1 -right-1 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-300 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-100"></span>
              </span>
            </div>
            <span className="text-sm font-semibold tracking-wide">MediCore AI Assistant</span>
          </button>
        )}
      </div>

      {/* Floating Chat Modal / Drawer */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-full max-w-lg bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all animate-in slide-in-from-bottom-5 duration-200" style={{ height: '620px' }}>
          {/* Header */}
          <div className="p-4 bg-gradient-to-r from-teal-900/80 via-slate-900 to-slate-900 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-teal-500/20 text-teal-400 border border-teal-500/30">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  MediCore AI Clinical Assistant
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-teal-500/10 text-teal-300 border border-teal-500/30">
                    VERTEX AI
                  </span>
                </h3>
                <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  Connected to Metropolitan Memorial Trauma Telemetry
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                title="Minimize"
              >
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick Action Suggestion Chips */}
          <div className="px-4 py-2.5 bg-slate-950/60 border-b border-slate-800/80 flex items-center gap-2 overflow-x-auto no-scrollbar">
            <span className="text-[10px] uppercase font-mono text-slate-500 shrink-0">Quick Queries:</span>
            {DEFAULT_SUGGESTIONS.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => handleSendQuery(suggestion)}
                className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-slate-800/90 hover:bg-teal-950/60 hover:text-teal-300 hover:border-teal-500/40 text-slate-300 border border-slate-700/60 shrink-0 transition-all"
              >
                {suggestion}
              </button>
            ))}
          </div>

          {/* Chat Messages Body */}
          <div className="flex-1 p-4 overflow-y-auto space-y-4">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex gap-3 ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {m.sender === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg bg-teal-600/20 text-teal-400 border border-teal-500/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Stethoscope className="w-4 h-4" />
                  </div>
                )}

                <div
                  className={`max-w-[85%] rounded-2xl p-3.5 text-xs leading-relaxed ${
                    m.sender === 'user'
                      ? 'bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-tr-none shadow-md'
                      : 'bg-slate-800/90 text-slate-200 border border-slate-700/60 rounded-tl-none shadow-md'
                  }`}
                >
                  <div className="whitespace-pre-line font-sans prose prose-invert prose-xs max-w-none">
                    {m.text}
                  </div>

                  {/* Optional Patient Context Action Button */}
                  {m.patientContext && (
                    <div className="mt-3 pt-2.5 border-t border-slate-700/60 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono text-teal-400">
                          {m.patientContext.name} ({m.patientContext.mrn})
                        </span>
                      </div>
                      {onOpenDocs && (
                        <button
                          onClick={() => onOpenDocs()}
                          className="flex items-center gap-1 text-[10px] font-mono text-teal-300 hover:text-white px-2 py-0.5 rounded bg-teal-500/20 border border-teal-500/30 transition-colors"
                        >
                          <FileText className="w-3 h-3" />
                          View EHR Note
                        </button>
                      )}
                    </div>
                  )}

                  <span className="block mt-1 text-[9px] text-right opacity-60">
                    {m.timestamp}
                  </span>
                </div>

                {m.sender === 'user' && (
                  <div className="w-7 h-7 rounded-lg bg-slate-700 text-slate-300 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-teal-600/20 text-teal-400 border border-teal-500/30 flex items-center justify-center shrink-0">
                  <Stethoscope className="w-4 h-4" />
                </div>
                <div className="bg-slate-800/90 border border-slate-700/60 rounded-2xl rounded-tl-none px-4 py-2.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce [animation-delay:0.2s]"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce [animation-delay:0.4s]"></span>
                  <span className="text-[11px] font-mono text-slate-400 ml-1.5">Synthesizing clinical data...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Footer Input */}
          <div className="p-3 bg-slate-950/80 border-t border-slate-800">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendQuery(inputValue);
              }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask clinical question, search patient note, or check vitals..."
                className="flex-1 bg-slate-900 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isTyping}
                className="p-2.5 rounded-xl bg-teal-600 hover:bg-teal-500 disabled:opacity-40 disabled:hover:bg-teal-600 text-white transition-colors"
                title="Send query"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
